import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material";

import { Resource } from "@impulse/sdp-shared-models";

import { BrowserTitleService } from "../../common/browser-title/browser-title.service";
import { TranslateService } from "../../common/translate/translate.service";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { SnackbarService } from "../../common/snackbar/snackbar.service";
import { PolicyWizardService } from "./policy-wizard/policy-wizard.service";
import { tableEditIcon } from "../../util";
import { GenericTable } from "../../genericTable";
import { SdpIcon } from "../../common/icon/models";
import { WizardDialogService } from "../wizard-dialog/wizard-dialog.service";
import { PolicyResourceSelectComponent } from "./policy-wizard";
import { PolicyAccessSelectComponent } from "./policy-wizard/policy-access-select/policy-access-select.component";
import { ApiGatewayService } from "app/common/api-gateway/api-gateway.service";
import { getResourceViewWithAccessList, ResourceViewWithAccessList } from "../resource-view";

@Component({
	selector: "sdp-app-policies",
	templateUrl: "./policies.component.html"
})
export class PoliciesComponent implements OnInit {
	public isDataLoaded = false;
	public data: ResourceViewWithAccessList[] | undefined = undefined;
	public roleList: string[] = [];
	public isEdit: boolean[] = [];
	public saveTimeout: any;

	public columnSchema: GenericTable.ColumnSchema[];

	private _showProgressBar = false;

	constructor(
		public dialog: MatDialog,
		private service: PolicyWizardService,
		private titleService: BrowserTitleService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService,
		private wizardDialogService: WizardDialogService,
		private apiGatewayService: ApiGatewayService
	) {
		this.columnSchema = this.getSchema();
	}

	public get showProgressBar() {
		return this._showProgressBar;
	}

	public async ngOnInit() {
		this.titleService.setTitle("assignApplications.header.title");
		this.isDataLoaded = false;
		await this.refresh();
	}

	private async refresh() {
		try {
			this._showProgressBar = true;
			const [policies, aliases] = await Promise.all([this.service.getAllPolicies(), this.apiGatewayService.getAllSamlAliases()]);
			this.data = getResourceViewWithAccessList(policies, aliases);
		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		} finally {
			this.isDataLoaded = true;
			this._showProgressBar = false;
		}
	}

	public deletePolicy(resource: Resource) {
		const message = this.translateService.translate(
			"assignApplications.delete.dialog.message",
			{
				applicationName: resource.name
			}
		);

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {});
		dialogRef.componentInstance.message = message;
		dialogRef.componentInstance.title = "assignApplications.delete.dialog.title";
		dialogRef.componentInstance.buttonAction = "sdp.label.delete";
		dialogRef.componentInstance.buttonCancel = "sdp.label.cancel";

		// This isn't actually a delete, we instead remove any access from the resource
		dialogRef.afterClosed().subscribe(async (result: boolean) => {
			if (result) {
				try {
					await this.service.deletePolicy(resource);
					this.snackbarService.open(this.translateService.translate("policy.delete.success", { name: resource.name }));
				} catch (e) {
					this.snackbarService.open(this.translateService.translate(e));
				}
				await this.refresh();
			}
		});
	}

	public showCreatePolicy() {
		this.service.newPolicy();
		this.openPolicyEditDialog(true);
	}

	public showEditPolicy(resource: Resource) {
		this.service.editPolicy(resource);
		this.openPolicyEditDialog(false);
	}

	private openPolicyEditDialog(isCreate: boolean) {
		const dialogRef = this.wizardDialogService.openDialog(
			isCreate ? PolicyResourceSelectComponent : PolicyAccessSelectComponent,
			{ type: "material", name: "assignment_turned_in", cssClass: "sdp-section-header-icon" },
			isCreate
		);
		dialogRef.afterClosed().subscribe(async () => {
			await this.refresh();
		});
	}

	private getSchema() {
		const columnSchema: GenericTable.ColumnSchema[] = [];

		columnSchema.push({
			minWidth: "30%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "applications.label.application"
			},
			bodySchema: {
				type: "string",
				boundField: "name"
			}
		});
		columnSchema.push({
			minWidth: "60%",
			type: "tag",
			headerSchema: {
				type: "basic",
				headerText: "assignApplications.grid.user-group",
				sortable: true
			},
			bodySchema: {
				type: "tag",
				boundField: "accessList"
			}
		});
		columnSchema.push({
			minWidth: "10%",
			type: "button",
			headerSchema: {
				type: "none",
			},
			bodySchema: {
				type: "button",
				buttons: this.getButtons()
			}
		});
		return columnSchema;
	}

	private getButtons() {
		const buttons: GenericTable.Button[] = [];
		buttons.push({
			type: "icon",
			icon: tableEditIcon,
			tooltip: "assignApplications.edit.tooltip",
			onClick: (row: Resource) => { return this.showEditPolicy(row); }
		});
		const deleteIcon: SdpIcon.Icon = { type: "material", name: "delete_sweep", cssClass: "sdp-icon-button-icon" };
		buttons.push({
			type: "icon",
			icon: deleteIcon,
			tooltip: "assignApplications.clear.tooltip",
			onClick: (row: Resource) => { return this.deletePolicy(row); }
		});
		return buttons;
	}

}
